import axios from "axios"
import {run_all_mocks} from "@/services/mocks"

export const BACK_URL = import.meta.env.VITE_API_URL || "http://127.0.0.1:8000"
export const api = axios.create({})

export function setupApiInterceptors(auth) {

  // ----- REQUEST -----
  api.interceptors.request.use(
    (config) => {
      const url = config.url || ''
      if (auth.access && url.startsWith(BACK_URL)) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${auth.access}`
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // ----- RESPONSE -----
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config || {}

      // 1) Fallo de red / CORS / timeout de axios
      if (!error.response) {
        await auth.logoutAndRedirect?.('network')
        return Promise.reject(new Error('Network error'))
      }

      const status = error.response.status
      const isAuthEndpoint =
        original.url?.includes('/users/login/') ||
        original.url?.includes('/users/refresh/') ||
        original.url?.includes('/users/verify/')

      // 2) 401 en endpoint NO-auth → intenta refresh 1 sola vez
      if (status === 401 && !isAuthEndpoint && !original._retry) {
        original._retry = true
        try {
          await auth.refreshToken()
          // El header Authorization se pondrá en el request interceptor
          return api.request(original)
        } catch {
          await auth.logoutAndRedirect?.('expired')
          return Promise.reject(new Error('Session expired'))
        }
      }

      // 3) 401 en endpoint de auth → no reintentes (evita bucles)
      if (status === 401 && isAuthEndpoint) {
        await auth.logoutAndRedirect?.('expired')
        return Promise.reject(error)
      }

      // 4) 419/498 → token corrupto/expirado (no 403, que es permiso denegado legítimo)
      if ([419, 498].includes(status)) {
        await auth.logoutAndRedirect?.('forbidden')
        return Promise.reject(error)
      }

      return Promise.reject(error)
    }
  )
}

// TODO: comenta o elimina en producción
run_all_mocks(api)

export class DefaultService {
  static resourceURL = ''
  static resourceApp = 'api/'

  static get serverURL() {
    return import.meta.env.VITE_API_URL || "http://127.0.0.1:8000"
  }

  static appURL(app) {
    return app ? `${this.serverURL}/${app}` : `${this.serverURL}/api`
  }
  static list(params = {}, headers = {}, responseType = null) {
    return this.execute('', 'GET', null, params, headers, responseType)
  }
  static retrieve(id, params = {}, headers = {}, responseType = null) {
    return this.execute(`${id}/`, 'GET', null, params, headers, responseType)
  }
  static create(data, params = {}, headers = {}, responseType = null) {
    return this.execute('', 'POST', data, params, headers, responseType)
  }
  static update(id, data, params = {}, headers = {}, responseType = null) {
    return this.execute(`${id}/`, 'PUT', data, params, headers, responseType)
  }
  static patch(id, data, params = {}, headers = {}, responseType = null) {
    return this.execute(`${id}/`, 'PATCH', data, params, headers, responseType)
  }
  static delete(id, params = {}, headers = {}, responseType = null) {
    return this.execute(`${id}/`, 'DELETE', null, params, headers, responseType)
  }
  static find(params = {}, headers = {}, responseType = null) {
    return this.execute('find/', 'GET', null, params, headers, responseType)
  }
  static archive(id, params = {}, headers = {}, responseType = null) {
    return this.execute(`${id}/archive/`, 'DELETE', null, params, headers, responseType)
  }
  static restore(id, params = {}, headers = {}, responseType = null) {
    return this.execute(`${id}/restore/`, 'GET', null, params, headers, responseType)
  }
  static batchDelete(ids, params = {}, headers = {}, responseType = null) {
    return this.execute('batch-delete/', 'DELETE', ids, params, headers, responseType)
  }
  static batchArchive(ids, params = {}, headers = {}, responseType = null) {
    return this.execute('batch-archive/', 'DELETE', ids, params, headers, responseType)
  }
  static batchRestore(ids, params = {}, headers = {}, responseType = null) {
    return this.execute('batch-restore/', 'DELETE', ids, params, headers, responseType)
  }
  static async execute(url, method, data = null, params = {}, headers = {}, responseType = null) {
    const fullUrl = `${this.appURL(this.resourceApp)}${this.resourceURL}${url}`
    return await api({
      method,
      url: fullUrl,
      data,
      params,
      headers,
      paramsSerializer: {indexes: null},
      responseType
    })
  }
}
